import { updateBusinessEntity } from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Input,
  Label,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import mixpanel, { MIXPANEL_SOURCE, MIXPANEL_SURFACE_AREA } from '@commenda/mixpanel';
import { IRegisteredAgent } from '@commenda/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Country } from '@prisma/client';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import AddressGroupRHF, {
  addressSchema,
} from '../../../../../components/AddressGroupRHF';
import { RequestCallbackDialog } from '../../../../../Contact/RequestCallbackModal';
import { useNotification } from '../../../../../hooks/useNotification';
import DisclaimerCheckbox from '../../../../common/DisclaimerCheckbox';

const addRegisteredAgentSchema = z.object({
  name: z.string().trim().min(1, { message: 'Name is required' }),
  address: addressSchema,
});

type AddRegisteredAgentSchemaFields = z.infer<typeof addRegisteredAgentSchema>;

const RegisteredAgentModal = ({
  isOpen,
  onClose,
  businessEntityId,
  onSuccess,
  registeredAgent,
}: {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId: number;
  onSuccess: () => void;
  registeredAgent?: IRegisteredAgent;
}) => {
  const isSubmitting = useToggle();

  const [hasConfirmed, setHasConfirmed] = useState(false);
  const requestCallbackModalVisibility = useToggle();

  const methods = useForm<AddRegisteredAgentSchemaFields>({
    resolver: zodResolver(addRegisteredAgentSchema),
    defaultValues: {
      address: {
        country: Country.US,
      },
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    reset,
  } = methods;

  const close = () => {
    reset();
    onClose();
  };

  useEffect(() => {
    if (registeredAgent) {
      reset({
        name: registeredAgent.name,
        address: registeredAgent.address,
      });
    }
  }, [registeredAgent]);

  const { setNotification } = useNotification();

  const onSubmit = async (data: AddRegisteredAgentSchemaFields) => {
    const address = {
      addressLine1: data.address.addressLine1,
      addressLine2: data.address.addressLine2 || '',
      addressLine3: data.address.addressLine3 || '',
      city: data.address.city,
      state: data.address.state,
      postalCode: data.address.postalCode,
      country: Country.US,
    };

    isSubmitting.on();

    const res = await updateBusinessEntity(businessEntityId, {
      countrySpecificFields: {
        US: {
          registeredAgentName: data.name,
          registeredAgentAddress: address,
        },
      },
    });

    if (!res.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to create registered agent.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: registeredAgent
        ? 'Registered agent updated successfully.'
        : 'Registered agent created successfully.',
    });

    mixpanel.track('ENTITY_REGISTERED_AGENT_UPDATED', {
      source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
      surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
      business_entity_id: businessEntityId,
      is_edit: !!registeredAgent,
      country: Country.US,
    });

    isSubmitting.off();
    onSuccess();
    close();
  };

  const hasRegisteredAgent = !!registeredAgent;

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent preventCloseOnOutsideClick>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>
                {hasRegisteredAgent
                  ? 'Edit Registered Agent Information'
                  : 'Add Registered Agent Information'}
              </DialogTitle>
            </DialogHeader>
            <RequestCallbackDialog
              isOpen={requestCallbackModalVisibility.state}
              onClose={requestCallbackModalVisibility.off}
            />
            <div className="p-3xl flex flex-col gap-2xl max-h-[440px] overflow-y-auto">
              <FieldWrapper>
                <Label>Name of registered agent</Label>
                <Input
                  placeholder="Enter name"
                  {...register('name')}
                  error={errors.name?.message}
                />
              </FieldWrapper>
              <AddressGroupRHF pathPrefix="address" hideCountry />

              <DisclaimerCheckbox
                isChecked={hasConfirmed}
                onCheckChange={setHasConfirmed}
              />
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                size="sm"
                onClick={close}
                type="button"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                type="submit"
                disabled={isSubmitting.visible || !hasConfirmed}
              >
                {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
                <span>Submit</span>
              </Button>
            </DialogFooter>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default RegisteredAgentModal;
